import { useState } from 'react'

import { useDispatch } from '../../shared/hooks/useDispatch'
import { useGuards } from '../../shared/hooks/useGuards'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Icon, Stack, Tooltip } from '@chakra-ui/react'
import { format } from 'date-fns'

import { Guards } from '@/components/icons'
import {
  Button,
  FormInputSelectControl,
  Textarea,
  VoiceRecorder,
} from '@/components/ui'
import { IncidentQueryResult } from '@/graphql/generated/hooks'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'

import { GuardsFormInputs } from '../../shared/types/guardActionTypes'
import { MixpanelDataIProps } from '../../shared/types/types'
import { getDispatchedGuardIds } from '../../shared/utils/guards'
import { DispatchMessage } from './DispatchMessage'
import { DispatchWhiteListAlert } from './DispatchWhiteListAlert'
import { NoAvailableGuardsAlert } from './NoAvailableGuardsAlert'
import { RadioGuardHeader } from './RadioGuardHeader'

interface DispatchGuardsIProps {
  incident?: IncidentQueryResult['data']['incident']
  mixpanelData?: MixpanelDataIProps
  onActionComplete?: () => void
}

export const DispatchGuards = ({
  incident,
  mixpanelData,
  onActionComplete,
}: DispatchGuardsIProps) => {
  const { t } = useTranslation()
  const { shouldEnableIncidentRadioGuards } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const [isLoading, setLoading] = useState(false)
  const [isRecording, setIsRecording] = useState(false)

  const {
    control,
    guards,
    guardsErrorMessage,
    guardsOptions,
    handleSubmit,
    hasSelectedGuards,
    isGuardsLoading,
    isGuardsValid,
  } = useGuards({ incident })

  const { audioFile, note, onNoteChange, onGenerateAudioFile, dispatch } =
    useDispatch({ incident })

  const hasGuards = isGuardsLoading || guardsOptions.length > 0

  const onSubmit: SubmitHandler<GuardsFormInputs> = async (values) => {
    setLoading(true)
    const dispatchedGuardIds: string[] = getDispatchedGuardIds(values)
    try {
      await dispatch(dispatchedGuardIds)
      trackEvent(`${BUTTON_PRESS} Dispatch Guards - Submit`, {
        ...mixpanelData,
        guard_ids: dispatchedGuardIds,
      })
      onActionComplete?.()
      showSuccess('Incident successfully dispatched.')
    } catch (error) {
      showError()
    } finally {
      setLoading(false)
    }
  }

  const onRecording = () => {
    trackEvent(`${BUTTON_PRESS} Dispatch - Recording`, mixpanelData)
  }

  const onCancel = () => {
    trackEvent(`${BUTTON_PRESS} Dispatch - Cancel Button`, mixpanelData)
    onActionComplete?.()
  }

  return (
    <Box mb={8}>
      <RadioGuardHeader
        dateTime={format(new Date(), 'MM/dd/yyyy')}
        guards={guards}
        header='Dispatch Guards'
        isGuardsLoading={isGuardsLoading}
        subHeader='Select Guard to dispatch'
      />

      <NoAvailableGuardsAlert
        hasGuards={hasGuards}
        isV1={true}
        mixpanelData={mixpanelData}
      />

      <Box mb='20px' mt='14px'>
        <FormInputSelectControl
          control={control}
          errorMessage={guardsErrorMessage}
          id='guards'
          isClearable
          isInvalid={!isGuardsValid}
          isMulti
          isSearchable
          options={guardsOptions}
          placeholder='Select Guards'
          rules={{
            required: true,
            validate: (values) => values.length !== 0,
          }}
          shouldAnimate={false}
        />
      </Box>

      <DispatchWhiteListAlert incident={incident} />

      <DispatchMessage
        noteTextarea={
          <Textarea
            id='dispatchNote'
            onChange={onNoteChange}
            placeholder='Add a comment...'
            value={note}
          />
        }
        voiceRecorder={
          <VoiceRecorder
            getAudioFile={onGenerateAudioFile}
            hasFile={!!audioFile}
            onRecording={onRecording}
            onRecordingChange={setIsRecording}
          />
        }
      />

      <Stack align='center' direction='row' mt={6} spacing={3}>
        <Button onClick={onCancel} variant='secondary'>
          Cancel
        </Button>
        <Tooltip
          hasArrow
          isDisabled={!isRecording}
          label={t('incidents:dispatch_recording_tooltip')}
          openDelay={250}
        >
          <Box as='span'>
            <Button
              isDisabled={
                isLoading ||
                !shouldEnableIncidentRadioGuards ||
                !hasSelectedGuards ||
                !isGuardsValid ||
                isRecording
              }
              isLoading={isLoading}
              leftIcon={<Icon as={Guards} boxSize={5} />}
              loadingText='Dispatching...'
              onClick={handleSubmit(onSubmit)}
            >
              Dispatch
            </Button>
          </Box>
        </Tooltip>
      </Stack>
    </Box>
  )
}
